import { criterion, fail, fromCriteria, pass, unsupported } from "@/core/check";
import type { Lesson } from "@/core/types";
import { istNackteAdresse, readHyperlinks } from "@/office/word/einfuegen";

const KEINE_API = unsupported(
  "Diese Word-Version stellt dem Add-in keine Hyperlinks zur Verfügung (ab WordApi 1.3), " +
    "deshalb lässt sich die Aufgabe hier nicht automatisch prüfen.",
  "Fahre mit der Maus über den Link und prüfe, ob Word das Ziel anzeigt. Danach kannst du den Schritt selbst als erledigt markieren.",
);

export const lektionHyperlinks: Lesson = {
  id: "hyperlinks",
  title: "Hyperlinks mit sprechendem Text",
  summary: "Ein Link sagt, wohin er führt – nicht, wie die Adresse lautet.",
  durationMinutes: 7,
  learningGoals: [
    "Hyperlinks über Einfügen → Link (Strg + K) anlegen",
    "Linktexte formulieren, die auch vorgelesen verständlich sind",
    "Links auf E-Mail-Adressen und Stellen im Dokument setzen",
  ],
  steps: [
    {
      id: "link-einfuegen",
      title: "Einen Link einfügen",
      explanation: [
        "Word verwandelt eine getippte Webadresse beim Leerzeichen automatisch in einen " +
          "Link. Das ist bequem, aber selten die beste Form: Im Text steht dann eine lange, " +
          "kryptische Adresse.",
        "Besser ist es, einen vorhandenen Text zum Link zu machen: Text markieren, " +
          "„Einfügen“ → „Link“ oder Strg + K, Adresse eintragen. Das Feld „Anzuzeigender " +
          "Text“ oben im Dialog enthält den markierten Text.",
        "Im selben Dialog stehen links drei weitere Ziele: eine andere Datei, eine Stelle " +
          "in diesem Dokument (Überschriften und Textmarken) und eine E-Mail-Adresse.",
      ],
      keyPoints: [
        "Text markieren, Strg + K, Adresse eintragen.",
        "Ziele: Webseite oder Datei, Stelle im Dokument, E-Mail-Adresse.",
        "Strg + Klick öffnet einen Link im Bearbeitungsmodus.",
      ],
      task: {
        instruction: "Füge in dein Dokument mindestens einen Hyperlink auf eine Webseite ein.",
        hint: "Ein Wort markieren, Strg + K, im Feld „Adresse“ zum Beispiel https://www.example.org eintragen.",
        check: async () => {
          const links = await readHyperlinks();
          if (links === null) return KEINE_API;
          return links.length > 0
            ? pass(`Der Link ist gesetzt (${links.length} im Dokument).`)
            : fail("Im Dokument ist noch kein Hyperlink.", "Strg + K öffnet den Dialog „Link einfügen“.");
        },
      },
    },
    {
      id: "sprechender-linktext",
      title: "Sprechender Linktext",
      explanation: [
        "Ein Screenreader liest Links gern gesammelt vor, damit man sich einen Überblick " +
          "verschaffen kann. „Hier klicken, hier klicken, h-t-t-p-s Doppelpunkt …“ hilft " +
          "dabei niemandem. Und auf Papier ist ein Link mit Adresse wertlos, wenn die Adresse " +
          "aus hundert Zeichen besteht.",
        "Ein guter Linktext beschreibt das Ziel: „Anmeldeformular für den Workshop“ statt " +
          "„hier“ oder der nackten Adresse. Den Text änderst du über Rechtsklick → „Link " +
          "bearbeiten“ im Feld „Anzuzeigender Text“.",
        "Wer eine Druckfassung braucht, in der die Adresse lesbar sein muss, schreibt eine " +
          "kurze Adresse ausdrücklich dazu – oder in eine Fußnote.",
      ],
      keyPoints: [
        "Der Linktext beschreibt das Ziel.",
        "Nie „hier klicken“, nie nackte Adressen.",
        "Rechtsklick → Link bearbeiten → Anzuzeigender Text.",
      ],
      task: {
        instruction:
          "Sorge dafür, dass jeder Link im Dokument einen sprechenden Text hat – keinen, der " +
          "nur die Adresse zeigt, und kein „hier“.",
        hint: "Rechtsklick auf den Link → „Link bearbeiten“ → oben den anzuzeigenden Text ändern.",
        check: async () => {
          const links = await readHyperlinks();
          if (links === null) return KEINE_API;
          if (links.length === 0) {
            return fail("Im Dokument ist noch kein Hyperlink.", "Leg zuerst einen Link an.");
          }
          const nackt = links.filter(istNackteAdresse);
          const hier = links.filter((link) => /^(hier|klick(e|en)?( hier)?|link|here|click here)$/i.test(link.text));
          return fromCriteria(
            [
              criterion(
                nackt.length === 0
                  ? "Kein Link zeigt die nackte Adresse"
                  : `Nackte Adresse als Linktext: ${nackt[0].text}`,
                nackt.length === 0,
              ),
              criterion(
                hier.length === 0 ? "Kein Link heißt nur „hier“" : `Nichtssagender Linktext: „${hier[0].text}“`,
                hier.length === 0,
              ),
            ],
            {
              success: "Jeder Link sagt, wohin er führt – vorgelesen wie gedruckt.",
              failure: "Mindestens ein Link hat noch keinen sprechenden Text.",
              hint: "Beschreibe das Ziel mit zwei bis fünf Wörtern.",
            },
          );
        },
      },
    },
    {
      id: "weitere-ziele",
      title: "E-Mail-Adressen und Stellen im Dokument",
      explanation: [
        "Ein Link auf eine E-Mail-Adresse öffnet beim Anklicken eine neue Nachricht. Im " +
          "Link-Dialog links „E-Mail-Adresse“ wählen; Word setzt „mailto:“ selbst davor.",
        "„Aktuelles Dokument“ listet alle Überschriften und Textmarken. Ein solcher Link " +
          "springt innerhalb des Dokuments – nützlich für lange Handbücher und für PDF-" +
          "Fassungen, in denen die Links erhalten bleiben. Wie Textmarken entstehen, zeigt " +
          "die nächste Lektion.",
      ],
      keyPoints: [
        "E-Mail-Adresse: Word ergänzt „mailto:“ selbst.",
        "Aktuelles Dokument: Sprung zu Überschrift oder Textmarke.",
      ],
    },
  ],
};
